import { pageTitle } from '@palaccount/config';
import type { TFunction } from 'i18next';
import { InviteUserRequestSchema, type InviteUserResponse, type OrgUser } from '@palaccount/contracts';
import { Copy, MoreHorizontal, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useCan } from '@/lib/permissions';
import { localizeServerMessage } from '@/lib/server-messages';
import { useMe } from '@/lib/session';
import { enterViewAs } from '@/lib/view-as';

import { AdminTabs } from './admin-tabs';
import {
  useAssignRole,
  useEffectivePermissions,
  useInviteUser,
  useOrgUsers,
  useResendInvite,
  useSecurityRoles,
  useTransferOwnership,
  useUserAction,
} from './api';

/**
 * ADM-05 — Users (PART 15.2): the list (avatar initials, name + LTR email,
 * role-or-"no role", status, owner badge, last active), the R18 invite box
 * (NO role field — the person arrives on the rung-4 floor), the row menu
 * (change/remove role, resend/revoke invitation, suspend/reactivate), and the
 * §15.2.10b ownership transfer with its typed-name confirmation.
 *
 * What the UI hides, the API refuses anyway:
 *  - every destructive item is ABSENT on the Owner's row (BR-ADM-05-05);
 *  - Suspend is absent on your own row (BR-ADM-05-04);
 *  - the Administrator option appears in the role picker for the OWNER only
 *    (BR-PERM-72/BR-ADM-06-21), and an admin's row offers no role change to
 *    a non-owner;
 *  - Transfer ownership exists only for the Owner, on other ACTIVE rows.
 *
 * The invitation IS emailed — queued in the same transaction as the invited
 * row, in the recipient's language. The activation link is still shown, with a
 * copy button, because under the `mock` email provider nothing leaves the
 * building and a copyable link is then the only way in. The note beside it
 * says which of the two is happening rather than leaving an administrator to
 * guess whether the person got anything.
 *
 * ERR-ADM-05-11 — "The person was added but we could not send the email. Use
 * Resend invitation on their row." The transactional enqueue means the invite
 * box itself can never half-succeed (users.service.ts §6a), so the sentence
 * appears where the failure actually happens: on the ROW, when the queued
 * send later dies permanently (`inviteUndeliverable`). The remedy the cell
 * names is the row menu's own Resend, which queues a fresh message and clears
 * the flag.
 *
 * BR-PERM-29 — §16.6A's visibility net. The Employment filter lists, on
 * demand, every account whose person is a terminated employee and every
 * account with no employee record at all; a terminated link is also badged on
 * the row unfiltered. The badge states the FACT about the employee record and
 * deliberately nothing about whether the account can still sign in — that
 * question is owner decision 24's open hole, and this screen is the net that
 * keeps such accounts findable, not the answer.
 */

const AVATAR_TONES = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-teal-100 text-teal-700',
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
}

function toneOf(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 997;
  return AVATAR_TONES[hash % AVATAR_TONES.length]!;
}

/**
 * ERR-ADM-05-02 / ERR-ADM-05-08 — "Typed something that is not an email" →
 * *"That does not look like an email address. It should look like
 * name@company.com."*
 *
 * ONE SENTENCE, TWO IDS, AND THAT IS THE SPECIFICATION'S OWN DOING: the row
 * appears in the ADM-05 screen's error table (spec line 32464) and again,
 * word for word, in the invitation box's own table (spec line 32232). This
 * build has exactly one email field on the screen — the box's — so one check
 * answers both rows rather than two checks answering one each.
 *
 * §6a field 1 is where the timing comes from: the address "must be a valid
 * address, must be unique across the company, and MUST BE CHECKED AS THE
 * PERSON TYPES RATHER THAN ON SUBMIT". §6a's Buttons table then greys **Send
 * invitation** "until the email and the name are filled in and the email is
 * both valid and unused". Both halves are below, and they are two different
 * requirements: the disabled button stops the request, the sentence tells the
 * person why.
 *
 * ── THE PREDICATE IS THE SERVER'S OWN, DELIBERATELY ────────────────────────
 *
 * `InviteUserRequestSchema.shape.email` is the field the API validates with.
 * Reaching for it here rather than writing a second regexp is the whole point:
 * a client-side pattern that disagreed with the server's would either refuse
 * an address the API would have taken, or admit one it refuses — and the
 * second is exactly the state this rule exists to prevent, where the person is
 * told nothing until a 400 arrives carrying zod's wording instead of the
 * spec's. There is one definition of "a valid address" in the product.
 *
 * THE BROWSER IS NOT THE CHECK, and `type="email"` stays anyway. It is kept
 * for the mobile keyboard and for `dir="ltr"`, and it cannot fight this:
 * the box is not a `<form>` and Send is not a submit button, so no native
 * validation bubble is ever raised. Before this, the attribute was the ONLY
 * thing on the field, which is why the person met the browser's sentence in
 * the browser's language rather than the spec's in theirs.
 */
export function isInvitableEmail(value: string): boolean {
  return InviteUserRequestSchema.shape.email.safeParse(value.trim()).success;
}

function statusClass(status: OrgUser['status']): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300';
    case 'invited':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
    default:
      return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300';
  }
}

function relativeTime(iso: string | null, t: TFunction): string {
  if (iso === null) return '—';
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return t('users.justNow');
  if (minutes < 60) return t('users.minAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('users.hrAgo', { count: hours });
  return t('users.dayAgo', { count: Math.round(hours / 24) });
}

export function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: users, isPending, error } = useOrgUsers();
  const { data: roles } = useSecurityRoles();
  const invite = useInviteUser();
  const resend = useResendInvite();
  const rowAction = useUserAction();
  const assignRole = useAssignRole();
  const transfer = useTransferOwnership();
  const effectivePermissions = useEffectivePermissions();

  const canEdit = useCan('admin.edit');
  const isOwner = me?.user.isOwner === true;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteResult, setInviteResult] = useState<InviteUserResponse | null>(null);
  const [roleEditFor, setRoleEditFor] = useState<OrgUser | null>(null);
  const [rolePick, setRolePick] = useState('');
  const [transferFor, setTransferFor] = useState<OrgUser | null>(null);
  const [transferName, setTransferName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * BR-PERM-29 — the on-demand listing. 'terminated' lists every account
   * whose person is a terminated employee; 'none' every account with no
   * employee record at all. Both facts arrive from the server's own read of
   * the employee↔account link, never derived in the browser.
   */
  const [employmentFilter, setEmploymentFilter] = useState<'all' | 'terminated' | 'none'>('all');

  useEffect(() => {
    document.title = pageTitle(t('users.title'));
  }, [t]);

  const fail = (cause: unknown) => {
    setFormError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));
  };

  /**
   * ERR-ADM-05-02 / ERR-ADM-05-08 — see `isInvitableEmail` above for why the
   * predicate is the server's own and why the timing is "as the person types".
   * `emailIsWrong` is what the SENTENCE keys on; `canSend` is what the BUTTON
   * keys on, and they are deliberately not the same expression: an untouched
   * box must leave Send greyed without telling anybody off for it.
   */
  const emailIsWrong = inviteEmail.trim() !== '' && !isInvitableEmail(inviteEmail);
  const canSend = inviteName.trim() !== '' && isInvitableEmail(inviteEmail);

  const submitInvite = async () => {
    setFormError(null);
    try {
      const result = await invite.mutateAsync({
        email: inviteEmail.trim(),
        fullName: inviteName.trim(),
        ...(inviteMessage.trim() === '' ? {} : { message: inviteMessage.trim() }),
      });
      setInviteResult(result);
      setInviteEmail('');
      setInviteName('');
      setInviteMessage('');
    } catch (cause) {
      fail(cause);
    }
  };

  /**
   * Roles the CALLER may assign: Active only; Administrator for the Owner alone.
   *
   * ABSENT rather than greyed, and this is the one place in the whole
   * permission sweep where BR-PERM-18 does not apply — because ADM-06 §11.2
   * says so directly, and says why: "The Administrator option must therefore
   * be **absent** from the role picker on the Users screen for everybody but
   * the Owner, NOT GREYED OUT: an admin has no legitimate use for seeing it."
   * BR-ADM-06-21 repeats it. The same paragraph governs Transfer ownership
   * (§10b step 1: "Nobody else sees this item, on any row") and Change role on
   * an administrator's row (BR-PERM-72). Converting any of the three to a
   * greyed control would break a numbered rule to satisfy another one.
   */
  const assignableRoles = (roles ?? []).filter(
    (role) => role.active && (isOwner || !(role.system && role.name === 'Administrator')),
  );

  const isAdminRow = (user: OrgUser) => user.roleName === 'Administrator';

  const submitRole = async () => {
    if (!roleEditFor) return;
    setFormError(null);
    try {
      await assignRole.mutateAsync({
        userId: roleEditFor.id,
        roleId: rolePick === '' ? null : rolePick,
      });
      setRoleEditFor(null);
    } catch (cause) {
      fail(cause);
    }
  };

  const submitTransfer = async () => {
    if (!transferFor) return;
    setFormError(null);
    if (transferName.trim() !== transferFor.name.trim()) {
      // §10b step 4 — re-read who you are handing the company to.
      setFormError(t('users.errTransferName'));
      return;
    }
    try {
      await transfer.mutateAsync({ userId: transferFor.id, confirmName: transferName.trim() });
      setTransferFor(null);
      setTransferName('');
    } catch (cause) {
      fail(cause);
    }
  };

  const act = async (user: OrgUser, action: 'suspend' | 'reactivate' | 'revoke-invite') => {
    setFormError(null);
    try {
      await rowAction.mutateAsync({ userId: user.id, action });
    } catch (cause) {
      fail(cause);
    }
  };

  const doResend = async (user: OrgUser) => {
    setFormError(null);
    try {
      setInviteResult(await resend.mutateAsync(user.id));
      setInviteOpen(true);
    } catch (cause) {
      fail(cause);
    }
  };

  /**
   * G-08 "View as" — fetch what the server says this person holds, hand it to
   * the browser-side preview (lib/view-as.ts), and start from the landing
   * screen, which is where they would start. The Owner's own session is
   * untouched; the banner the shell draws is the way back.
   */
  const startViewAs = async (user: OrgUser) => {
    setFormError(null);
    try {
      const result = await effectivePermissions.mutateAsync(user.id);
      enterViewAs({ userId: result.userId, name: result.name, permissions: result.permissions });
      void navigate('/');
    } catch (cause) {
      fail(cause);
    }
  };

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {localizeServerMessage(t, error.message)}
      </p>
    );
  }

  return (
    <div className="grid gap-4">
      <AdminTabs />
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto">
          <h2 className="text-lg font-bold">{t('users.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('users.subtitle')}</p>
        </div>
        {/* BR-PERM-29 — the select's resting label doubles as "no filter". */}
        <NativeSelect
          aria-label={t('users.employmentFilter')}
          value={employmentFilter}
          onChange={(event) => setEmploymentFilter(event.target.value as 'all' | 'terminated' | 'none')}
          className="w-auto"
        >
          <option value="all">{t('users.employmentFilter')}</option>
          <option value="terminated">{t('users.employmentTerminated')}</option>
          <option value="none">{t('users.employmentNone')}</option>
        </NativeSelect>
        <Gated permission="admin.create" reasonKey="perm.cannot.inviteUsers">
          <Button
            onClick={() => {
              setInviteOpen(true);
              setInviteResult(null);
              setFormError(null);
            }}
          >
            <Plus aria-hidden /> {t('users.invite')}
          </Button>
        </Gated>
      </div>

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}

      {inviteOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('users.inviteTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {inviteResult ? (
              <div className="grid gap-2">
                <p className="text-sm">{t('users.inviteEmailed', { email: inviteResult.user.email })}</p>
                {/* The fallback, and it says so: with the mock provider the
                    email is recorded rather than delivered. */}
                <p className="text-xs text-amber-700 dark:text-amber-400">{t('users.linkNote')}</p>
                <div className="flex items-center gap-2">
                  <Input readOnly dir="ltr" value={inviteResult.activationPath} data-testid="activation-link" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void navigator.clipboard?.writeText(inviteResult.activationPath)}
                  >
                    <Copy aria-hidden /> {t('users.copyLink')}
                  </Button>
                </div>
                <div>
                  <Button variant="outline" onClick={() => setInviteOpen(false)}>
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="inv-email">{t('users.fEmail')}</Label>
                    <Input
                      id="inv-email"
                      dir="ltr"
                      type="email"
                      placeholder="name@company.com"
                      value={inviteEmail}
                      aria-invalid={emailIsWrong}
                      aria-describedby={emailIsWrong ? 'inv-email-error' : undefined}
                      onChange={(event) => setInviteEmail(event.target.value)}
                    />
                    {/*
                      ERR-ADM-05-02 / ERR-ADM-05-08, beside the field it is
                      about (GEN-152). Shown only once something has been
                      typed: an empty box is not a mistake, it is a box nobody
                      has reached yet, and greeting the reader with a refusal
                      before they have touched anything is the failure §6a
                      guards against from the other side.
                    */}
                    {emailIsWrong ? (
                      <p id="inv-email-error" role="alert" className="text-xs text-destructive">
                        {t('users.errNotEmail')}
                      </p>
                    ) : null}
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="inv-name">{t('users.fName')}</Label>
                    <Input
                      id="inv-name"
                      value={inviteName}
                      onChange={(event) => setInviteName(event.target.value)}
                    />
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="inv-msg">{t('users.fMessage')}</Label>
                  <Textarea
                    id="inv-msg"
                    rows={2}
                    maxLength={300}
                    value={inviteMessage}
                    onChange={(event) => setInviteMessage(event.target.value)}
                  />
                </div>
                {/* R18: an invitation carries NO role — and the box says so. */}
                <p className="text-xs text-muted-foreground">{t('users.noRoleNote')}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => void submitInvite()}
                    // §6a Buttons: greyed "until the email and the name are
                    // filled in and the email is both valid and unused". The
                    // UNUSED half is the server's — it is the only party that
                    // can know — and arrives as ERR-ADM-05-01/-07's sentence.
                    disabled={invite.isPending || !canSend}
                  >
                    {t('users.send')}
                  </Button>
                  <Button variant="outline" onClick={() => setInviteOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {roleEditFor ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('users.roleTitle', { name: roleEditFor.name })}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="role-pick">{t('users.colRole')}</Label>
              <NativeSelect
                id="role-pick"
                className="max-w-72"
                value={rolePick}
                onChange={(event) => setRolePick(event.target.value)}
              >
                <option value="">{t('users.noRole')}</option>
                {assignableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => void submitRole()} disabled={assignRole.isPending}>
                {t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => setRoleEditFor(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {transferFor ? (
        <Card className="border-amber-300">
          <CardHeader>
            <CardTitle>{t('users.transferTitle', { name: transferFor.name })}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {/* §10b step 3 — what changes, in plain words. */}
            <p className="text-sm">{t('users.transferBody', { name: transferFor.name })}</p>
            <div className="grid gap-1.5">
              <Label htmlFor="transfer-name">{t('users.transferType', { name: transferFor.name })}</Label>
              <Input
                id="transfer-name"
                value={transferName}
                onChange={(event) => setTransferName(event.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => void submitTransfer()}
                disabled={transfer.isPending || transferName.trim() === ''}
              >
                {t('users.transferConfirm')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setTransferFor(null);
                  setTransferName('');
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.colName')}</TableHead>
                <TableHead>{t('users.colRole')}</TableHead>
                <TableHead>{t('users.colStatus')}</TableHead>
                <TableHead>{t('users.colLastActive')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : null}
              {(users ?? [])
                .filter((user) => employmentFilter === 'all' || user.employment === employmentFilter)
                .map((user) => {
                const self = user.id === me?.user.id;
                // BR-PERM-72: an admin's row answers to the Owner alone.
                const adminProtected = isAdminRow(user) && !isOwner;
                const menuItems: { key: string; label: string; onClick: () => void }[] = [];
                if (canEdit && !user.isOwner && !adminProtected) {
                  if (user.status === 'invited') {
                    menuItems.push(
                      {
                        key: 'resend',
                        label: t('users.actResend'),
                        onClick: () => void doResend(user),
                      },
                      {
                        key: 'revoke',
                        label: t('users.actRevoke'),
                        onClick: () => void act(user, 'revoke-invite'),
                      },
                    );
                  }
                  if (user.status === 'active') {
                    // BR-ADM-06-08 — "A user must not change their own role."
                    // Absent on your own row, exactly like Suspend below, and
                    // for the same reason: an action nobody may take should
                    // not be offered and then refused. THE UI IS NOT THE WALL —
                    // `RolesService.assign` refuses it for every route,
                    // including the ones that never draw a menu.
                    if (!self) {
                      menuItems.push({
                        key: 'role',
                        label: t('users.actChangeRole'),
                        onClick: () => {
                          setRoleEditFor(user);
                          setRolePick(user.roleId ?? '');
                          setFormError(null);
                        },
                      });
                      if (user.roleId !== null) {
                        menuItems.push({
                          key: 'removeRole',
                          label: t('users.actRemoveRole'),
                          onClick: () => void assignRole.mutateAsync({ userId: user.id, roleId: null }).catch(fail),
                        });
                      }
                    }
                    if (!self) {
                      menuItems.push({
                        key: 'suspend',
                        label: t('users.actSuspend'),
                        onClick: () => void act(user, 'suspend'),
                      });
                    }
                  }
                  if (user.status === 'suspended') {
                    menuItems.push({
                      key: 'reactivate',
                      label: t('users.actReactivate'),
                      onClick: () => void act(user, 'reactivate'),
                    });
                  }
                }
                // §10b: the Owner alone, on somebody else's ACTIVE row.
                if (isOwner && !user.isOwner && user.status === 'active') {
                  // G-08: a read-only preview of the product through this
                  // person's permissions. Absent while a preview is on (isOwner
                  // reads false then); the shell's banner is the way out.
                  menuItems.push({
                    key: 'viewAs',
                    label: t('users.actViewAs'),
                    onClick: () => void startViewAs(user),
                  });
                  menuItems.push({
                    key: 'transfer',
                    label: t('users.actTransfer'),
                    onClick: () => {
                      setTransferFor(user);
                      setTransferName('');
                      setFormError(null);
                    },
                  });
                }

                return (
                  <TableRow key={user.id} data-testid={`user-row-${user.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span
                          aria-hidden
                          className={`flex size-9 items-center justify-center rounded-full text-xs font-semibold ${toneOf(user.id)}`}
                        >
                          {initialsOf(user.name)}
                        </span>
                        <span className="grid">
                          <span className="flex items-center gap-2 text-sm font-semibold">
                            {user.name}
                            {user.isOwner ? (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                                {t('users.ownerBadge')}
                              </span>
                            ) : null}
                            {/* BR-PERM-29 — the FACT that this account's
                                person is a terminated employee, visible
                                without the filter. Neutral on purpose: whether
                                the account can still sign in is decision 24's
                                open question, and this badge must not imply
                                the answer. */}
                            {user.employment === 'terminated' ? (
                              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                {t('users.employmentTerminated')}
                              </span>
                            ) : null}
                          </span>
                          <span dir="ltr" className="text-xs text-muted-foreground">
                            {user.email}
                          </span>
                          {/* ERR-ADM-05-11 — the row-level partial success:
                              added, but the invitation email died in the
                              dispatcher. The sentence names the remedy, and
                              the remedy is on this row's own menu. */}
                          {user.inviteUndeliverable ? (
                            <span role="alert" className="text-xs text-destructive">
                              {t('users.inviteUndeliverable')}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.roleName ?? (
                        <span className="text-muted-foreground">{t('users.noRole')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(user.status)}`}
                      >
                        {t(`users.status.${user.status}`)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {relativeTime(user.lastActiveAt, t)}
                    </TableCell>
                    <TableCell className="text-end">
                      {menuItems.length > 0 ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" aria-label={t('users.rowMenu', { name: user.name })}>
                              <MoreHorizontal aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {menuItems.map((item) => (
                              <DropdownMenuItem key={item.key} onSelect={item.onClick}>
                                {item.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
